import { useStoreCaDetail } from '@gozoki/api'
import {
  STORE_CA_DETAIL_PERIODS,
  type StoreCaDetailPeriod,
  type StoreCaDetailResponse,
  type StoreScoreComparison,
} from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { Skeleton } from '@gozoki/ui/components/ui/skeleton'
import { cn } from '@gozoki/ui/lib/utils'
import { type ReactNode, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { ShoppingBagIcon } from './icons/ShoppingBagIcon'
import { TrendBadge } from './ui/DashboardCard'
import { KioskModal, ModalBody, ModalKpi, ModalPanel } from './ui/KioskModal'

/** Couleurs des trois séries du graphe (maquette « CA 7 jours »). */
const SERIES = {
  previous: '#8DAA7E',
  current: '#536B4A',
  next: '#C7BCCB',
} as const

type Tone = 'good' | 'warning' | 'danger'

const TONE_COLOR: Record<Tone, string> = {
  good: '#6A9560',
  warning: '#E0922F',
  danger: '#E25B53',
}

const CHART_HEIGHT = 230

/** Graduations rondes de l'axe (pas de 1, 2, 2,5 ou 5 × 10ⁿ), 5 intervalles environ. */
const niceTicks = (max: number, intervals = 5): number[] => {
  if (max <= 0) return [0]
  const rawStep = max / intervals
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const step =
    [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((value) => value >= rawStep) ??
    10 * magnitude
  const top = Math.ceil(max / step) * step
  return Array.from({ length: Math.round(top / step) + 1 }, (_, index) => index * step)
}

interface PeriodCopy {
  tab: string
  title: string
  headline: string
  legend: { previous: string; current: string; next: string }
  /** Étiquettes au-dessus des barres ; `null` quand elles surchargeraient le graphe (24 h). */
  barTags: { previous: string; current: string; next: string } | null
  /** Libellés des deux périodes comparées par les cartes de score. */
  scoreRows: { current: string; previous: string }
  scoreTags: { current: string; previous: string }
  versus: string
}

const PERIODS: Record<StoreCaDetailPeriod, PeriodCopy> = {
  // Les scores ne sont calculés qu'à la clôture de la journée : « Actuel » montre ceux d'hier (vol
  // affiché seulement une fois connu, après l'inventaire du lendemain).
  day: {
    tab: 'Actuel',
    title: 'CA DU JOUR',
    headline: 'CA du jour',
    legend: { previous: 'Même jour S-1', current: 'Aujourd’hui', next: 'Prévision du jour' },
    barTags: null,
    scoreRows: { current: 'Hier', previous: 'Même jour S-1' },
    scoreTags: { current: 'Hier', previous: 'S-1' },
    versus: 'vs S-1',
  },
  week: {
    tab: '7 Jours',
    title: 'CA 7 JOURS',
    headline: 'Moyenne sur 7 Jours',
    legend: {
      previous: '7 Jours précédents',
      current: '7 Jours actuels',
      next: '7 Jours suivants',
    },
    barTags: { previous: 'S-1', current: 'S-0', next: 'S+1' },
    scoreRows: { current: 'Semaine actuelle', previous: 'Semaine précédente' },
    scoreTags: { current: 'S-0', previous: 'S-1' },
    versus: 'vs S-1',
  },
  month: {
    tab: '30 Jours',
    title: 'CA 30 JOURS',
    headline: 'Moyenne sur 30 Jours',
    legend: {
      previous: '30 Jours précédents',
      current: '30 Jours actuels',
      next: '30 Jours suivants',
    },
    barTags: { previous: 'M-1', current: 'M-0', next: 'M+1' },
    scoreRows: { current: '30 derniers jours', previous: '30 jours précédents' },
    scoreTags: { current: 'M-0', previous: 'M-1' },
    versus: 'vs M-1',
  },
}

const euros = (value: number) => `${frenchFormat(Math.round(value))} €`
const eurosCents = (value: number) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
const percent = (ratio: number) =>
  `${(ratio * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`
const fifoPoints = (value: number | null) => (value === null ? '—' : `${Math.round(value)} / 100`)

const difference = (score: StoreScoreComparison): number | null =>
  score.current !== null && score.previous !== null ? score.current - score.previous : null

/** Classes de couleur du badge de tendance, selon le sens (bon / à surveiller). */
const TONE_TEXT: Record<Tone, string> = {
  good: 'text-[#6A9560]',
  warning: 'text-[#E0922F]',
  danger: 'text-[#E25B53]',
}

/** Badge de tendance du dashboard (`TrendBadge`), coloré selon le sens de la variation. */
function Trend({
  value,
  suffix,
  versus,
  tone,
}: {
  value: number | null
  suffix: string
  versus: string
  tone: Tone
}) {
  return (
    <TrendBadge
      value={value}
      suffix={suffix}
      versus={versus}
      size={24}
      className={TONE_TEXT[tone]}
    />
  )
}

function PeriodTabs({
  value,
  onChange,
}: {
  value: StoreCaDetailPeriod
  onChange: (period: StoreCaDetailPeriod) => void
}) {
  return (
    <div className="flex w-fit items-center gap-[2px] rounded-[8px] border border-[#0000001A] bg-[#F0EDE2] p-[3px]">
      {STORE_CA_DETAIL_PERIODS.map((period) => (
        <button
          key={period}
          type="button"
          onClick={() => onChange(period)}
          aria-pressed={period === value}
          className={cn(
            'cursor-pointer rounded-[6px] px-[10px] py-[6px] font-sans text-[14px] leading-[16px] font-semibold',
            period === value ? 'bg-[#536B4A] text-[#F9F8F1]' : 'text-[#3E4F3F]',
          )}
        >
          {PERIODS[period].tab}
        </button>
      ))}
    </div>
  )
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-[6px]">
      <span className="size-[14px] rounded-[4px]" style={{ backgroundColor: color }} />
      <span className="font-sans text-[12px] leading-[14px] font-medium text-[#3E4F3F]">
        {label}
      </span>
    </span>
  )
}

/** Étiquette « S-1 / S-0 / S+1 » posée au-dessus de chaque barre non vide. */
function barTag(text: string | undefined) {
  if (!text) return null
  return (
    <LabelList
      position="top"
      offset={6}
      formatter={(value) => (value == null ? '' : text)}
      style={{ fill: '#6C7A6B', fontSize: 11, fontWeight: 600 }}
    />
  )
}

function CaChart({ data, copy }: { data: StoreCaDetailResponse; copy: PeriodCopy }) {
  const max = Math.max(
    0,
    ...data.bars.flatMap((bar) => [bar.previous ?? 0, bar.current ?? 0, bar.next ?? 0]),
  )
  const ticks = niceTicks(max)
  const seriesKeys = data.hasForecast
    ? (['previous', 'current', 'next'] as const)
    : (['previous', 'current'] as const)

  // `font-sans` : les textes SVG de recharts héritent de la police du conteneur.
  return (
    <div className="flex flex-col gap-[4px] font-sans">
      <span className="font-sans text-[10px] leading-[12px] font-medium text-[#3E4F3F]">CA</span>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.bars} margin={{ top: 20, right: 0, bottom: 0, left: 0 }} barGap={4}>
            <CartesianGrid vertical={false} stroke="#0000000F" />
            <YAxis
              width={48}
              tickLine={false}
              axisLine={false}
              ticks={ticks}
              domain={[0, ticks.at(-1) ?? 0]}
              tick={{ fontSize: 10, fill: '#3E4F3F' }}
              tickFormatter={(value: number) => frenchFormat(Math.round(value))}
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#3E4F3F' }}
            />
            {seriesKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={SERIES[key]}
                radius={[8, 8, 0, 0]}
                maxBarSize={36}
                isAnimationActive={false}
              >
                {barTag(copy.barTags?.[key])}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function ScoreCardFrame({
  title,
  value,
  trend,
  children,
}: {
  title: string
  value: string
  trend: ReactNode
  children: ReactNode
}) {
  return (
    <ModalPanel
      wave="corner"
      className="flex min-w-0 flex-1 flex-col justify-between gap-[12px] p-[17px]"
    >
      <div className="flex flex-col gap-[6px]">
        <span className="font-sans text-[15px] leading-[18px] font-semibold text-[#354A31]">
          {title}
        </span>
        <div className="flex items-center gap-[14px]">
          <span className="font-brand text-[28px] leading-[32px] font-extrabold whitespace-nowrap text-[#354A31]">
            {value}
          </span>
          {trend}
        </div>
      </div>
      {children}
    </ModalPanel>
  )
}

function Gauge({ label, value, fill }: { label: string; value: number | null; fill: string }) {
  return (
    <div className="flex flex-col gap-[4px]">
      <div className="flex items-center justify-between font-sans text-[12px] leading-[14px] font-medium text-[#3E4F3F]">
        <span>{label}</span>
        <span>{fifoPoints(value)}</span>
      </div>
      <div className="h-[7px] w-full overflow-hidden rounded-[2px] bg-[#D9E6D1]">
        <div
          className="h-full rounded-[2px]"
          style={{ width: `${Math.max(0, Math.min(100, value ?? 0))}%`, backgroundColor: fill }}
        />
      </div>
    </div>
  )
}

/** Carte Score FIFO : valeur, variation, et deux jauges (période actuelle / précédente). */
function FifoCard({ score, copy }: { score: StoreScoreComparison; copy: PeriodCopy }) {
  const trend = difference(score)

  return (
    <ScoreCardFrame
      title="Score FIFO"
      value={fifoPoints(score.current)}
      trend={
        <Trend
          value={trend === null ? null : Math.round(trend)}
          suffix=" pts"
          versus={copy.versus}
          tone={trend === null || trend >= 0 ? 'good' : 'warning'}
        />
      }
    >
      <div className="flex flex-col gap-[8px]">
        <Gauge label={copy.scoreRows.current} value={score.current} fill="#536B4A" />
        <Gauge label={copy.scoreRows.previous} value={score.previous} fill="#A4C494" />
      </div>
    </ScoreCardFrame>
  )
}

function RateBar({
  tag,
  value,
  max,
  color,
}: {
  tag: string
  value: number | null
  max: number
  color: string
}) {
  return (
    <div className="flex w-[76px] flex-col items-center gap-[4px]">
      <span className="font-sans text-[12px] leading-[14px] font-medium text-[#6C7A6B]">{tag}</span>
      <div
        className="flex w-full items-center justify-center rounded-t-[8px] font-sans text-[13px] leading-[16px] font-semibold text-[#F9F8F1]"
        style={{
          backgroundColor: color,
          height: value === null || max === 0 ? 26 : Math.max(26, (value / max) * 40),
        }}
      >
        {value === null ? '—' : percent(value)}
      </div>
    </div>
  )
}

/**
 * Carte d'un taux (casse, vol) : valeur, variation, et deux barres période précédente / actuelle.
 * La barre de la période actuelle prend la couleur d'alerte du taux quand il a monté.
 */
function RateCard({
  title,
  score,
  alertTone,
  copy,
}: {
  title: string
  score: StoreScoreComparison
  alertTone: Exclude<Tone, 'good'>
  copy: PeriodCopy
}) {
  const trend = difference(score)
  const worse = trend !== null && trend > 0
  const max = Math.max(score.current ?? 0, score.previous ?? 0)

  return (
    <ScoreCardFrame
      title={title}
      value={score.current === null ? '—' : percent(score.current)}
      trend={
        <Trend
          value={trend === null ? null : Math.round(trend * 1000) / 10}
          suffix="%"
          versus={copy.versus}
          tone={worse ? alertTone : 'good'}
        />
      }
    >
      <div className="flex items-end justify-end gap-[4px] border-b border-[#0000001A]">
        <RateBar
          tag={copy.scoreTags.previous}
          value={score.previous}
          max={max}
          color={SERIES.previous}
        />
        <RateBar
          tag={copy.scoreTags.current}
          value={score.current}
          max={max}
          color={worse ? TONE_COLOR[alertTone] : SERIES.current}
        />
      </div>
    </ScoreCardFrame>
  )
}

function CaDetailBody({ data, copy }: { data: StoreCaDetailResponse; copy: PeriodCopy }) {
  return (
    <>
      <div className="flex items-start justify-between gap-[24px]">
        <div className="flex flex-col gap-[4px]">
          <span className="font-sans text-[14px] leading-[16px] font-medium text-[#6C7A6B]">
            {copy.headline}
          </span>
          <div className="flex items-center gap-[16px]">
            <span className="font-brand text-[40px] leading-[44px] font-extrabold whitespace-nowrap text-[#354A31]">
              {euros(data.headlineCa)}
            </span>
            <Trend
              value={data.vsPreviousPercent}
              suffix="%"
              versus={copy.versus}
              tone={
                data.vsPreviousPercent === null || data.vsPreviousPercent >= 0 ? 'good' : 'warning'
              }
            />
          </div>
        </div>

        <div className="flex items-stretch gap-[20px] pt-[4px]">
          <ModalKpi
            label="Panier moyen"
            value={data.averageCart === null ? '—' : eurosCents(data.averageCart)}
            color="#6A9560"
          />
          <span className="w-px bg-[#0000001A]" />
          <ModalKpi
            label="Tickets générés"
            value={frenchFormat(data.ticketCount)}
            color="#354A31"
          />
        </div>
      </div>

      <div className="flex items-center gap-[24px]">
        <LegendItem color={SERIES.previous} label={copy.legend.previous} />
        <LegendItem color={SERIES.current} label={copy.legend.current} />
        {data.hasForecast ? <LegendItem color={SERIES.next} label={copy.legend.next} /> : null}
      </div>

      <CaChart data={data} copy={copy} />
    </>
  )
}

/**
 * Modale « CA 7 jours » (maquette validée par le client le 14/10), ouverte depuis le chevron de la
 * carte C.A 7 JOURS : CA moyen par jour de la période, panier moyen, tickets, graphe par jour de
 * semaine (période précédente, actuelle, prévision de la suivante), puis les trois scores de la
 * période comparés à la précédente. Les onglets Actuel / 7 Jours / 30 Jours changent la période ;
 * « Actuel » détaille la journée en cours heure par heure.
 */
export function CaDetailModal({ onClose }: { onClose: () => void }) {
  const [period, setPeriod] = useState<StoreCaDetailPeriod>('week')
  const { data, isError } = useStoreCaDetail(period)
  const copy = PERIODS[period]

  return (
    <KioskModal
      icon={<ShoppingBagIcon className="size-[24px]" />}
      title={copy.title}
      ariaLabel="Détail du chiffre d'affaires"
      onClose={onClose}
      className="w-[1150px]"
    >
      <div className="flex flex-col gap-[12px]">
        <ModalPanel wave="wide" className="flex flex-col gap-[16px] p-[17px]">
          <PeriodTabs value={period} onChange={setPeriod} />

          <ModalBody
            data={data}
            isError={isError}
            errorLabel="Chiffre d’affaires indisponible pour le moment"
            errorHeight={CHART_HEIGHT + 130}
            skeleton={
              <div className="flex flex-col gap-[16px]" aria-busy>
                <Skeleton className="h-[64px] w-[360px]" />
                <Skeleton className="h-[14px] w-[480px]" />
                <Skeleton className="w-full" style={{ height: CHART_HEIGHT + 18 }} />
              </div>
            }
          >
            {(loaded) => <CaDetailBody data={loaded} copy={copy} />}
          </ModalBody>
        </ModalPanel>

        <div className="flex h-[168px] items-stretch gap-[12px]">
          {data ? (
            <>
              <FifoCard score={data.scores.fifoScore} copy={copy} />
              <RateCard
                title="Taux de casse"
                score={data.scores.tauxCasse}
                alertTone="warning"
                copy={copy}
              />
              <RateCard
                title="Taux de vol"
                score={data.scores.tauxVol}
                alertTone="danger"
                copy={copy}
              />
            </>
          ) : (
            Array.from({ length: 3 }, (_, index) =>
              isError ? (
                <ModalPanel key={index} className="min-w-0 flex-1" />
              ) : (
                <Skeleton key={index} className="min-w-0 flex-1 rounded-[16px]" />
              ),
            )
          )}
        </div>
      </div>
    </KioskModal>
  )
}
